import { formatInTimeZone } from "date-fns-tz";
import type { User } from "features/user/types";

const BILLING_FORMAT = "EEE, dd MMM yyyy";
const BILLING_FORMAT_COMPACT = "dd MMM yyyy";

/**
 * Formats a billing/renewal date for display.
 *
 * Uses `formatInTimeZone(..., "UTC", ...)` rather than `format(...)` because
 * Chargebee timestamps are UTC: a `next_billing_at` that corresponds to
 * 00:00 UTC rolls to the previous day's date for any browser in a negative
 * offset (Americas), which would display the wrong calendar date against
 * the server-side schedule. Locking to UTC keeps the displayed date
 * identical across every user's timezone and matches the "charged on this
 * day" reality of the Chargebee schedule.
 *
 * Format preserves the legacy moment output (`"Mon, 05 Apr 2026"`) so
 * existing billing copy renders unchanged.
 */
export const formatBillingDate = (d: Date): string =>
  formatInTimeZone(d, "UTC", BILLING_FORMAT);

/**
 * Compact variant without the day-of-week prefix ("05 Apr 2026"). Matches
 * the older `moment(...).format("DD MMM YYYY")` call sites used inside
 * narrow alerts and table rows where the leading weekday added width
 * without value.
 */
export const formatBillingDateCompact = (d: Date): string =>
  formatInTimeZone(d, "UTC", BILLING_FORMAT_COMPACT);

/**
 * Shared guarded converter for Chargebee unix-seconds timestamps.
 * Returns null for null / undefined / zero / negative inputs so callers
 * don't have to re-implement the check.
 *
 * Named `unixToDate` rather than `toDate` to avoid shadowing
 * `date-fns.toDate` — the name collision is silent and only surfaces as a
 * bug the day someone adds a `from "date-fns"` import to this module.
 */
export const unixToDate = (ts?: number | null): Date | null =>
  typeof ts === "number" && ts > 0 ? new Date(ts * 1000) : null;

/**
 * Convenience: guarded timestamp → formatted billing date, with an
 * em-dash placeholder when the timestamp is missing. Used by upsell
 * surfaces where a Chargebee estimate can carry `0` / null for dates
 * that don't yet apply (e.g. `next_billing_at` on a `non_renewing` sub).
 */
export const formatUnixBillingDate = (ts?: number | null): string => {
  const d = unixToDate(ts);
  return d ? formatBillingDate(d) : "—";
};

/** Compact ("05 Apr 2026") variant of `formatUnixBillingDate`. */
export const formatUnixBillingDateCompact = (ts?: number | null): string => {
  const d = unixToDate(ts);
  return d ? formatBillingDateCompact(d) : "—";
};

/**
 * Pure (non-React) helper that mirrors the server-side `accessEndsAt`
 * derivation in `/plan/v2/user-context` — collapses the cancellation
 * state matrix into a single "when does the user lose access?" Date
 * the UI can render directly. Raw timestamps remain on the subscription
 * object for surfaces that need state-specific copy (e.g. "trial ends"
 * vs "billing period ends").
 *
 * Returns null when no end-date applies — an active non-cancelled
 * customer has no fixed access-end date, so the caller should render
 * "Renews on {nextBillingAt}" copy instead.
 */
export const getAccessEndsAt = (user: User | null | undefined): Date | null => {
  const sub = user?.subscription?.subscription;
  if (!sub) return null;

  if (sub.status === "non_renewing") return unixToDate(sub.current_term_end);
  if (sub.status === "in_trial" && sub.cancelled_at) {
    // `trial_end` is authoritative for when a cancelled trial loses access.
    // If it's missing (legacy/malformed subs) return null rather than
    // falling back to `cancelled_at` — cancelled_at is the request time,
    // not the access-end time, so substituting it would render a
    // misleading date. Callers already gate copy on a non-null accessEndsAt.
    return unixToDate(sub.trial_end);
  }
  if (sub.status === "active" && sub.scheduled_cancellation_date)
    return unixToDate(sub.scheduled_cancellation_date);
  return null;
};

/**
 * The next renewal date for an active subscription. Null if the user
 * is in a trial, cancelled, or doesn't have an active subscription.
 */
export const getNextRenewalAt = (
  user: User | null | undefined
): Date | null => {
  const sub = user?.subscription?.subscription;
  if (!sub || sub.status !== "active") return null;
  if (sub.scheduled_cancellation_date) return null;
  return unixToDate(sub.next_billing_at);
};

/**
 * End of the user's current billing period — used by cancel flows to
 * preview "you'll keep access until X" before the user confirms cancel.
 * Differs from accessEndsAt: this is always the current paid term end
 * (even for active, non-cancelled subs), whereas accessEndsAt is only
 * populated once cancellation is actually scheduled.
 */
export const getCurrentTermEnd = (
  user: User | null | undefined
): Date | null => {
  const sub = user?.subscription?.subscription;
  if (!sub) return null;
  if (sub.status !== "active" && sub.status !== "non_renewing") return null;
  return unixToDate(sub.current_term_end);
};

export type BillingTimingState =
  | "trial-active"
  | "trial-cancelled"
  | "paid-active"
  | "paid-cancel-scheduled"
  | "paid-non-renewing"
  | "paused"
  | "cancelled"
  | "unknown";

export const getBillingTimingState = (
  user: User | null | undefined
): BillingTimingState => {
  const sub = user?.subscription?.subscription;
  if (!sub) return "unknown";
  const status = sub.status;

  if (status === "in_trial") {
    return sub.cancelled_at ? "trial-cancelled" : "trial-active";
  }
  if (status === "non_renewing") return "paid-non-renewing";
  if (status === "active") {
    return sub.scheduled_cancellation_date
      ? "paid-cancel-scheduled"
      : "paid-active";
  }
  if (status === "cancelled") return "cancelled";
  // Explicit case for Chargebee's "paused" status. Callers that render
  // end-date copy currently fall through to null for this state (no
  // end-date applies while paused), but surfacing the status prevents a
  // silent regression if downstream code needs to distinguish it later.
  if (status === "paused") return "paused";
  return "unknown";
};
